import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { User } from '../../services/authService';
import { taskService, Task, TaskFormData } from '../../services/taskService';
import { tagService, Tag } from '../../services/tagService';
import { projectService } from '../../services/projectService';
import { Project } from '../../types/Project';
import TagSelector from './TagSelector';
import TaskRow from './TaskRow';
import TaskForm from './TaskForm';
import TaskTimer from '../timer/TaskTimer';
import './TaskManagement.css';

interface TaskManagementProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

const TaskManagement: React.FC<TaskManagementProps> = ({ user, onClose, onToggleSidebar }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [filter, setFilter] = useState<'today' | 'tomorrow' | 'not_assigned' | 'completed' | 'uncompleted' | 'past_uncompleted'>('today');
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [taskTags, setTaskTags] = useState<{ [taskId: number]: string[] }>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [creatingTask, setCreatingTask] = useState(false);
  const [showDatePickerModal, setShowDatePickerModal] = useState(false);
  const [taskToMove, setTaskToMove] = useState<Task | null>(null);
  const [selectedMoveDate, setSelectedMoveDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showTimerModal, setShowTimerModal] = useState(false);
  const [taskForTimer, setTaskForTimer] = useState<Task | null>(null);

  useEffect(() => {
    if (user?.id) {
      loadTasks();
      loadAvailableTags();
      loadProjects();
    }
  }, [user, filter, searchQuery]);

  // Check for create param from quick command or project link
  useEffect(() => {
    const createParam = searchParams.get('create');
    const projectIdParam = searchParams.get('project_id');
    if (createParam === 'true') {
      setShowAddForm(true);
      // Remove the params from URL
      searchParams.delete('create');
      if (projectIdParam) {
        searchParams.delete('project_id');
      }
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const loadAvailableTags = async () => {
    try {
      const response = await tagService.getAllTags();
      if (response.success && response.tags) {
        setAvailableTags(response.tags);
      }
    } catch (error) {
      console.error('Error loading available tags:', error);
    }
  };

  const loadProjects = async () => {
    if (!user?.id) return;
    try {
      const response = await projectService.getUserProjects({
        user_id: user.id.toString()
      });
      if (response.success && response.projects) {
        const convertedProjects = response.projects.map(project => projectService.convertDatabaseProject(project));
        setProjects(convertedProjects);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  // Helper function to get project name from project_id
  const getProjectName = (projectId?: number): string | undefined => {
    if (!projectId) return undefined;
    const project = projects.find(p => p.id === projectId.toString());
    return project?.name;
  };

  const loadTasks = async () => {
    if (!user?.id) return;

    try {
      setLoading(true);
      setError(null);

      const response = await taskService.getUserTasks({ user_id: user.id.toString() });

      if (response.success && response.tasks) {
        let filteredTasks = response.tasks;
        
        // Apply client-side filtering based on the selected filter
        if (filter === 'today') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          filteredTasks = response.tasks.filter(task => {
            if (!task.assigned_date) return false;
            const assignedDate = new Date(task.assigned_date);
            assignedDate.setHours(0, 0, 0, 0);
            return assignedDate.getTime() === today.getTime();
          });
        } else if (filter === 'tomorrow') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          const tomorrow = new Date(today);
          tomorrow.setDate(tomorrow.getDate() + 1);
          
          filteredTasks = response.tasks.filter(task => {
            if (!task.assigned_date) return false;
            const assignedDate = new Date(task.assigned_date);
            assignedDate.setHours(0, 0, 0, 0);
            return assignedDate.getTime() === tomorrow.getTime();
          });
        } else if (filter === 'not_assigned') {
          filteredTasks = response.tasks.filter(task => !task.assigned_date);
        } else if (filter === 'completed') {
          filteredTasks = response.tasks.filter(task => task.status === 'completed');
        } else if (filter === 'uncompleted') {
          filteredTasks = response.tasks.filter(task => task.status !== 'completed');
        } else if (filter === 'past_uncompleted') {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          filteredTasks = response.tasks.filter(task => {
            // Must be uncompleted
            if (task.status === 'completed') return false;
            
            // Must have an assigned date in the past
            if (!task.assigned_date) return false;
            
            const assignedDate = new Date(task.assigned_date);
            assignedDate.setHours(0, 0, 0, 0);
            
            return assignedDate.getTime() < today.getTime();
          });
        }
        
        // Apply search filter if search query exists
        if (searchQuery.trim()) {
          const query = searchQuery.toLowerCase().trim();
          filteredTasks = filteredTasks.filter(task => {
            const titleMatch = task.title?.toLowerCase().includes(query);
            const descriptionMatch = task.description?.toLowerCase().includes(query);
            return titleMatch || descriptionMatch;
          });
        }
        
        setTasks(filteredTasks);
        
        // Extract tags directly from task response
        const tagsMap: { [taskId: number]: string[] } = {};
        for (const task of filteredTasks) {
          tagsMap[task.id] = task.tags || [];
        }
        setTaskTags(tagsMap);
      } else {
        setError('Failed to load tasks');
        setTaskTags({});
      }
    } catch (err) {
      console.error('Error loading tasks:', err);
      setError('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTask = async (taskData: TaskFormData & { tag_names?: string[] }) => {
    if (!user?.id) return;

    setCreatingTask(true);
    try {
      const response = await taskService.createTask(user.id.toString(), taskData);
      
      if (response.success) {
        setShowAddForm(false);
        setSuccessMessage('Task created successfully!');
        setTimeout(() => setSuccessMessage(null), 3000);
        await loadTasks();
      } else {
        setError(response.message || 'Failed to create task');
      }
    } catch (err) {
      console.error('Error creating task:', err);
      setError('Failed to create task');
    } finally {
      setCreatingTask(false);
    }
  };

  const handleToggleTaskStatus = async (task: Task) => {
    try {
      const newStatus = task.status === 'completed' ? 'todo' : 'completed';
      const response = await taskService.updateTask({
        id: task.id.toString(),
        status: newStatus
      });

      if (response.success) {
        await loadTasks();
      } else {
        setError(response.message || 'Failed to update task');
      }
    } catch (err) {
      console.error('Error updating task:', err);
      setError('Failed to update task');
    }
  };

  const handleTaskClick = (taskId: number) => {
    navigate(`/tasks/${taskId}`);
  };

  // Get today's date in YYYY-MM-DD format
  const getToday = (): string => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Calculate tomorrow's date (using local date to avoid timezone issues)
  const calculateTomorrow = (assignedDate?: string): string => {
    const baseDate = assignedDate ? assignedDate : getToday();
    const [year, month, day] = baseDate.split('-').map(Number);
    const tomorrow = new Date(year, month - 1, day);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowYear = tomorrow.getFullYear();
    const tomorrowMonth = String(tomorrow.getMonth() + 1).padStart(2, '0');
    const tomorrowDay = String(tomorrow.getDate()).padStart(2, '0');
    return `${tomorrowYear}-${tomorrowMonth}-${tomorrowDay}`;
  };

  // Calculate next week (same day, next week) - using local date to avoid timezone issues
  const calculateNextWeek = (currentDate: string): string => {
    const [year, month, day] = currentDate.split('-').map(Number);
    const nextWeek = new Date(year, month - 1, day);
    nextWeek.setDate(nextWeek.getDate() + 7);
    const nextWeekYear = nextWeek.getFullYear();
    const nextWeekMonth = String(nextWeek.getMonth() + 1).padStart(2, '0');
    const nextWeekDay = String(nextWeek.getDate()).padStart(2, '0');
    return `${nextWeekYear}-${nextWeekMonth}-${nextWeekDay}`;
  };

  // Calculate Saturday of the same week - using local date to avoid timezone issues
  const calculateSaturdayOfSameWeek = (currentDate: string): string => {
    const [year, month, day] = currentDate.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
    const daysToAdd = dayOfWeek === 6 ? 0 : 6 - dayOfWeek;
    date.setDate(date.getDate() + daysToAdd);
    const saturdayYear = date.getFullYear();
    const saturdayMonth = String(date.getMonth() + 1).padStart(2, '0');
    const saturdayDay = String(date.getDate()).padStart(2, '0');
    return `${saturdayYear}-${saturdayMonth}-${saturdayDay}`;
  };

  // Handle task menu actions
  const handleTaskMenuAction = async (task: Task, action: 'today' | 'tomorrow' | 'next-week' | 'backlog' | 'custom-date' | 'delete' | 'log-time') => {
    if (!user) return;

    try {
      if (action === 'delete') {
        if (!window.confirm('Delete this task?')) return;
        
        const response = await taskService.deleteTask(task.id.toString());
        
        if (response.success) {
          await loadTasks();
          setSuccessMessage('Task deleted successfully!');
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          setError('Failed to delete task. Please try again.');
        }
      } else {
        let newDate: string | null = null;
        const baseDate = task.assigned_date || getToday();
        
        if (action === 'today') {
          newDate = getToday();
        } else if (action === 'tomorrow') {
          newDate = calculateTomorrow(baseDate);
        } else if (action === 'next-week') {
          newDate = calculateNextWeek(baseDate);
        } else if (action === 'backlog') {
          newDate = null; // Remove assigned_date
        } else if (action === 'custom-date') {
          // Open date picker modal
          setTaskToMove(task);
          // Default to task's assigned_date if it exists, otherwise today
          const defaultDate = task.assigned_date || getToday();
          setSelectedMoveDate(defaultDate);
          setShowDatePickerModal(true);
          return; // Exit early, will handle update in modal
        } else if (action === 'log-time') {
          // Open timer modal
          setTaskForTimer(task);
          setShowTimerModal(true);
          return; // Exit early, timer modal handles its own logic
        }
        
        const updateData: any = {
          id: task.id.toString()
        };
        
        if (action === 'backlog') {
          updateData.assigned_date = null;
        } else if (newDate) {
          updateData.assigned_date = newDate;
        }
        
        const response = await taskService.updateTask(updateData);
        
        if (response.success) {
          await loadTasks();
          
          const actionMessage = action === 'backlog' 
            ? 'Task moved to backlog' 
            : action === 'today'
            ? `Task assigned to today (${newDate})`
            : action === 'tomorrow'
            ? `Task rescheduled to tomorrow (${newDate})`
            : `Task rescheduled to next week (${newDate})`;
          
          setSuccessMessage(actionMessage);
          setTimeout(() => setSuccessMessage(null), 3000);
        } else {
          setError(`Failed to update task: ${response.message || 'Please try again.'}`);
        }
      }
    } catch (error) {
      console.error('Error handling task menu action:', error);
      setError(`Failed to update task: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  // Handle moving task to custom date
  const handleMoveTaskToDate = async () => {
    if (!user || !taskToMove || !selectedMoveDate) return;

    try {
      const updateData: any = {
        id: taskToMove.id.toString(),
        assigned_date: selectedMoveDate
      };

      const response = await taskService.updateTask(updateData);

      if (response.success) {
        await loadTasks();
        
        // Format date for display
        const [year, month, day] = selectedMoveDate.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        const formattedDate = date.toLocaleDateString('en-US', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        });
        
        setSuccessMessage(`Task moved to ${formattedDate}`);
        setTimeout(() => setSuccessMessage(null), 3000);
        
        // Close modal and reset state
        setShowDatePickerModal(false);
        setTaskToMove(null);
        setSelectedMoveDate('');
      } else {
        setError(`Failed to move task: ${response.message || 'Please try again.'}`);
      }
    } catch (error) {
      console.error('Error moving task to date:', error);
      setError(`Failed to move task: ${error instanceof Error ? error.message : 'Please try again.'}`);
    }
  };

  // Cancel date picker modal
  const cancelDatePicker = () => {
    setShowDatePickerModal(false);
    setTaskToMove(null);
    setSelectedMoveDate('');
  };

  // Handle timer modal close
  const handleTimerClose = () => {
    setShowTimerModal(false);
    setTaskForTimer(null);
  };

  // Handle time log created callback
  const handleTimeLogCreated = () => {
    setSuccessMessage('Time logged successfully!');
    setTimeout(() => setSuccessMessage(null), 3000);
    handleTimerClose();
  };

  if (!user) {
    return (
      <div className="task-management">
        <div className="task-header">
          <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
          <h1>✓ Tasks</h1>
        </div>
        <div className="tm-auth-required">
          <p>Please sign in to manage your tasks</p>
        </div>
      </div>
    );
  }

  return (
    <div className="task-management">
      <div className="task-header">
        <button className="sidebar-toggle" onClick={onToggleSidebar}>☰</button>
        <h1>✓ Tasks</h1>
        <button className="add-task-btn" onClick={() => setShowAddForm(!showAddForm)}>
          {showAddForm ? '✕' : '+'}
        </button>
      </div>

      {error && (
        <div className="tm-error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {successMessage && (
        <div className="success-message" style={{
          background: '#d4edda',
          border: '1px solid #c3e6cb',
          color: '#155724',
          padding: '0.75rem 1rem',
          margin: '1rem 1.5rem',
          borderRadius: '0.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          fontSize: '0.875rem'
        }}>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} style={{
            background: 'none',
            border: 'none',
            color: '#155724',
            cursor: 'pointer',
            fontSize: '1rem',
            padding: 0
          }}>×</button>
        </div>
      )}

      {showAddForm && (
        <div className="add-task-form">
          <TaskForm
            onSubmit={handleAddTask}
            onCancel={() => setShowAddForm(false)}
            submitButtonText="Save Task"
            cancelButtonText="Cancel"
            errorMessage={error}
            showError={true}
            showSuccess={false}
            isLoading={creatingTask}
            projects={projects}
            userId={user?.id}
            initialData={searchParams.get('project_id') ? {
              title: '',
              description: '',
              status: 'todo',
              project_id: Number(searchParams.get('project_id'))
            } : undefined}
          />
        </div>
      )}

      <div className="task-search-container">
        <input
          type="text"
          placeholder="Search tasks..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="task-search-input"
        />
      </div>

      <div className="task-filters">
        <button 
          className={`filter-btn ${filter === 'past_uncompleted' ? 'active' : ''}`}
          onClick={() => setFilter('past_uncompleted')}
        >
          Past Uncompleted
        </button>
        <button 
          className={`filter-btn ${filter === 'today' ? 'active' : ''}`}
          onClick={() => setFilter('today')}
        >
          Today
        </button>
        <button 
          className={`filter-btn ${filter === 'tomorrow' ? 'active' : ''}`}
          onClick={() => setFilter('tomorrow')}
        >
          Tomorrow
        </button>
        <button 
          className={`filter-btn ${filter === 'not_assigned' ? 'active' : ''}`}
          onClick={() => setFilter('not_assigned')}
        >
          Not assigned
        </button>
        <button 
          className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
          onClick={() => setFilter('completed')}
        >
          Completed
        </button>
        <button 
          className={`filter-btn ${filter === 'uncompleted' ? 'active' : ''}`}
          onClick={() => setFilter('uncompleted')}
        >
          Uncompleted
        </button>
      </div>

      <div className="tm-task-list-container">
        <div className="tm-task-list">
          {loading ? (
            <div className="tm-loading-state">
              <div className="tm-loading-spinner"></div>
              <p>Loading tasks...</p>
            </div>
          ) : tasks.length === 0 ? (
            <div className="tm-empty-state">
              <p>No tasks found</p>
              <button onClick={() => setShowAddForm(true)} className="tm-add-first-task-btn">
                Add your first task
              </button>
            </div>
          ) : (
            tasks.map((task) => (
              <TaskRow
                key={task.id}
                task={task}
                onToggleStatus={handleToggleTaskStatus}
                onTaskClick={handleTaskClick}
                onMenuAction={handleTaskMenuAction}
                taskTags={taskTags[task.id] || []}
                availableTags={availableTags}
                showMenu={true}
                showStatusToggle={true}
                projectName={getProjectName(task.project_id)}
              />
            ))
          )}
        </div>
      </div>

      {/* Date Picker Modal for Moving Task */}
      {showDatePickerModal && taskToMove && (
        <div className="calendar-drawer-overlay" onClick={cancelDatePicker}>
          <div className="date-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #e0e0e0',
              background: '#f9f9f9'
            }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#333333', fontWeight: 500 }}>Move Task to Date</h3>
              <button className="drawer-close" onClick={cancelDatePicker} style={{
                background: 'none',
                border: 'none',
                fontSize: '1.25rem',
                cursor: 'pointer',
                padding: '0.25rem',
                borderRadius: '50%',
                color: '#666666',
                transition: 'background-color 0.2s'
              }} onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'} onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>×</button>
            </div>
            <div className="drawer-content" style={{ padding: '1.5rem' }}>
              <div className="date-picker-content">
                <div className="date-picker-task-info">
                  <span className="date-picker-task-title">{taskToMove.title}</span>
                </div>
                <div className="date-selector">
                  <label htmlFor="move-date-input-tm" className="date-selector-label">
                    Select Date:
                  </label>
                  <input
                    id="move-date-input-tm"
                    type="date"
                    value={selectedMoveDate}
                    onChange={(e) => setSelectedMoveDate(e.target.value)}
                    className="date-selector-input"
                  />
                </div>
                <div className="date-picker-actions">
                  <button
                    className="date-picker-cancel-btn"
                    onClick={cancelDatePicker}
                  >
                    Cancel
                  </button>
                  <button
                    className="date-picker-confirm-btn"
                    onClick={handleMoveTaskToDate}
                    disabled={!selectedMoveDate}
                  >
                    Move Task
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Timer Modal for Logging Time */}
      {showTimerModal && taskForTimer && user && (
        <div className="calendar-drawer-overlay" onClick={handleTimerClose}>
          <div className="date-picker-modal" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header" style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #e0e0e0',
              background: '#f9f9f9'
            }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#333333', fontWeight: 500 }}>Log Time for {taskForTimer.title}</h3>
              <button className="drawer-close" onClick={handleTimerClose} style={{
                background: 'none',
                border: 'none',
                fontSize: '1.25rem',
                cursor: 'pointer',
                padding: '0.25rem',
                borderRadius: '50%',
                color: '#666666',
                transition: 'background-color 0.2s'
              }} onMouseEnter={(e) => e.currentTarget.style.background = '#f0f0f0'} onMouseLeave={(e) => e.currentTarget.style.background = 'none'}>×</button>
            </div>
            <div className="drawer-content" style={{ padding: '1.5rem' }}>
              <TaskTimer
                task={taskForTimer}
                user={user}
                onTimeLogCreated={handleTimeLogCreated}
                onClose={handleTimerClose}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskManagement;
